import { useMutation } from "@apollo/client/react";
import {
  SelfAssessmentCancelMutation,
  SelfAssessmentCancelMutationVariables,
  SelfAssessmentStarterForFolder,
  SelfAssessmentStarterForFolderVariables,
  SelfAssessmentStarterForFolder_selfAssessmentForFolder,
  SelfAssessmentStarterMutation,
  SelfAssessmentStarterMutationVariables,
  SelfAssessmentStarterMutation_startSelfAssessment_selfAssessment,
  SelfAssessmentStatus,
} from "__generated__/types";
import { Button } from "common/buttons";
import { SimpleToast } from "common/components/SimpleToast";
import { SubText } from "common/components/Typography";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import gql from "graphql-tag";
import * as React from "react";
import { useNavigate } from "react-router";

const MUTATION_START = gql`
  mutation SelfAssessmentStarterMutation($input: StartSelfAssessmentInput!) {
    startSelfAssessment(input: $input) {
      selfAssessment {
        id
        articles {
          id
          content {
            id
            title
            youtubeVideoId
            body {
              content
              isMarkdown
            }
            relevantContent {
              content
              isMarkdown
            }
          }
        }
        multipleChoiceExercises {
          id
          content {
            title
            id
          }
        }
      }
    }
  }
`;

const MUTATION_END = gql`
  mutation SelfAssessmentCancelMutation($input: CancelSelfAssessmentInput!) {
    cancelSelfAssessment(input: $input) {
      selfAssessment {
        id
        originFolderId
      }
    }
  }
`;

const DATA_QUERY = gql`
  query SelfAssessmentStarterForFolder($id: ID!) {
    selfAssessmentForFolder(id: $id) {
      id
      originFolderId
      status
      delivery {
        deliveredAt
        score {
          factorPercent
        }
      }
      articles {
        id
        content {
          id
          title
          youtubeVideoId
          body {
            content
            isMarkdown
          }
          relevantContent {
            content
            isMarkdown
          }
        }
      }
      multipleChoiceExercises {
        id
        content {
          title
          id
        }
      }
    }
  }
`;

interface Props {
  id: string;
  canStart: boolean;
}

export const SelfAssessmentStarter: React.FC<
  React.PropsWithChildren<Props>
> = ({ id, canStart }) => {
  const navigate = useNavigate();
  const [showNoExerciseFoundError, setShowNoExerciseFoundError] =
    React.useState(false);

  const { data, refetch } = useSimpleQuery<
    SelfAssessmentStarterForFolder,
    SelfAssessmentStarterForFolderVariables
  >(DATA_QUERY, {
    variables: {
      id,
    },
  });

  const selfAssessmentInstance = data?.selfAssessmentForFolder;
  const selfAssessmentInstanceId = selfAssessmentInstance?.id;

  const [startMutate, { error: startError }] = useMutation<
    SelfAssessmentStarterMutation,
    SelfAssessmentStarterMutationVariables
  >(MUTATION_START);

  const [cancelMutate, { error: cancelError, loading: cancelLoading }] =
    useMutation<
      SelfAssessmentCancelMutation,
      SelfAssessmentCancelMutationVariables
    >(MUTATION_END);

  const redirectToFirstElement = (
    data:
      | SelfAssessmentStarterMutation_startSelfAssessment_selfAssessment
      | SelfAssessmentStarterForFolder_selfAssessmentForFolder
      | null
      | undefined,
  ) => {
    const firstArticleElement = data?.articles[0]?.id;
    const firstMultipleChoiceElement = data?.multipleChoiceExercises[0]?.id;

    if (data?.id) {
      if (firstArticleElement) {
        navigate(urls.selfAssessmentArticle(firstArticleElement, data.id));
      } else if (firstMultipleChoiceElement) {
        navigate(
          urls.selfAssessmentQuestion(firstMultipleChoiceElement, data.id),
        );
      } else {
        setShowNoExerciseFoundError(true);
      }
    }
  };

  const startSelfAssessment = async () => {
    const result = await startMutate({
      variables: {
        input: {
          selfAssessmentExercisesFolderId: id,
        },
      },
    });

    if (result.data) {
      await refetch();

      redirectToFirstElement(result.data.startSelfAssessment.selfAssessment);
    }
  };

  const continueSelfAssessment = () => {
    redirectToFirstElement(selfAssessmentInstance);
  };

  const cancelSelfAssessment = async () => {
    if (selfAssessmentInstanceId) {
      const result = await cancelMutate({
        variables: {
          input: {
            selfAssessmentId: selfAssessmentInstanceId,
          },
        },
      });

      if (result.data) {
        await refetch();
      }
    }
  };

  const errorMessage = showNoExerciseFoundError ? (
    <SimpleToast
      timeoutMs={60000}
      message={
        "Det er ikke lagt til noen oppgaver for denne selvtesten. Vennligst ta kontakt med undervisningsansvarlig."
      }
    />
  ) : null;

  switch (selfAssessmentInstance?.status) {
    case SelfAssessmentStatus.DELIVERED:
      return (
        <>
          {errorMessage}
          <SubText>
            Din beste score er{" "}
            {(selfAssessmentInstance.delivery?.score.factorPercent ?? 0) * 100}%
            riktig
          </SubText>
          <Button onClick={startSelfAssessment}>Prøv på nytt</Button>
        </>
      );
    case SelfAssessmentStatus.IN_PROGRESS:
      return (
        <>
          {errorMessage}
          <Button
            loading={cancelLoading}
            onClick={cancelSelfAssessment}
            kind="secondary"
          >
            Avslutt selvtest
          </Button>
          <Button onClick={continueSelfAssessment}>Fortsett på selvtest</Button>
          <MutationLoadingOrError loading={false} error={cancelError} />
        </>
      );
    case SelfAssessmentStatus.CANCELED:
    default:
      return (
        <>
          {errorMessage}
          <Button
            loading={cancelLoading}
            kind={canStart ? "primary" : "disabled"}
            onClick={startSelfAssessment}
          >
            {canStart ? "Start selvtest" : "Kan ikke startes"}
          </Button>
          <MutationLoadingOrError loading={false} error={startError} />
        </>
      );
  }
};
